import { useEffect } from 'react';
import { apiClient } from '@packages/api-client';

// ---- ALLOWED_ORIGIN：postMessage 目标域名 ----
// 三级优先：config.js → 环境变量 → 硬编码默认值
const configParentOrigin = (globalThis as any)._template_app_server_config?.parentOrigin;
const ALLOWED_ORIGIN =
  (typeof configParentOrigin === 'string' && configParentOrigin.startsWith('http')
    ? configParentOrigin
    : null) ||
  import.meta.env.VITE_PARENT_ORIGIN ||
  'https://intelliu.aialign.cn';

// ---- 从 URL 读取 token ----
interface UrlAuthPayload {
  token: string;
  refreshToken?: string;
}

const getAuthFromUrl = (): UrlAuthPayload | null => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  if (!token) return null;
  return {
    token,
    refreshToken: params.get('refresh_token') ?? undefined,
  };
};

// ---- 嵌入模式判断 ----
export const isEmbedMode = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'embed' || sessionStorage.getItem('intellix_embed_mode') === 'true';
};

// ---- 将 URL token 写入 localStorage ----
const applyAuthFromUrl = (payload: UrlAuthPayload): void => {
  if (!payload.token) return;

  // 去 Bearer 前缀
  const rawToken = payload.token.replace(/^Bearer\s+/i, '');
  console.log('[ParentAuthSync] 写入 auth_token，前20字符:', rawToken.substring(0, 20));

  apiClient.setAuthToken(rawToken);

  if (payload.refreshToken) {
    const rawRefreshToken = payload.refreshToken.replace(/^Bearer\s+/i, '');
    apiClient.setRefreshToken(rawRefreshToken);
  }

  // 通知 ProtectedRoute 重新检查认证状态
  window.dispatchEvent(new CustomEvent('auth:parent-sync'));
};

// ---- 模块级 URL token 初始化（在 React 渲染前执行） ----
const initUrlAuth = (): boolean => {
  const payload = getAuthFromUrl();
  if (!payload) return false;

  applyAuthFromUrl(payload);

  // 清除 URL 中的 token/refresh_token 参数，保留 mode=embed
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  url.searchParams.delete('refresh_token');
  window.history.replaceState({}, '', url.toString());

  // 嵌入模式持久化到 sessionStorage
  if (isEmbedMode()) {
    sessionStorage.setItem('intellix_embed_mode', 'true');
  }

  return true;
};

// 模块级执行：确保 token 在 ProtectedRoute 的 useState 初始化之前已写入 localStorage
initUrlAuth();

// ---- 嵌入模式 401 处理：通知 parent 重定向 ----
export const handleEmbedUnauthorized = (): void => {
  if (!isEmbedMode()) return;

  apiClient.clearAuthToken();

  if (window.parent !== window) {
    window.parent.postMessage(
      {
        type: 'INTELLIX_AUTH_EXPIRED',
        payload: {
          message: 'Token expired or invalid',
          currentUrl: window.location.href,
        },
      },
      ALLOWED_ORIGIN
    );
  }
};

// ---- React hook：安全兜底，确保 URL token 已被处理 ----
export const useParentAuthSync = (): void => {
  useEffect(() => {
    // 模块级 initUrlAuth 已执行，这里作为安全兜底
    initUrlAuth();
  }, []);
};

export default useParentAuthSync;
